<script lang="ts">
  // Aba de Torneios: lista unica de "Proximos". Leonardo 2026-05-31
  // pediu pra tirar "Em andamento" e "Historico" — menos feature, menos
  // chance de delatar bot. Auto-refresh a cada 20s; pausa quando a aba
  // nao ta visivel.
  import { onMount, onDestroy } from 'svelte';
  import {
    upcomingTournaments,
    tournamentsLoading,
    tournamentsError,
    loadAllTournaments,
    loadIsAdmin,
    isAdmin,
  } from '../../stores/tournaments';
  import TournamentTile from './TournamentTile.svelte';
  import CreateTournamentModal from './CreateTournamentModal.svelte';
  import { Plus } from 'lucide-svelte';

  let showCreate = $state(false);

  const AUTO_REFRESH_MS = 20_000;
  let intervalId: number | null = null;

  function tick() {
    if (typeof document !== 'undefined' && document.hidden) return;
    void loadAllTournaments();
  }

  onMount(() => {
    void loadAllTournaments();
    void loadIsAdmin();
    intervalId = window.setInterval(tick, AUTO_REFRESH_MS);
  });

  onDestroy(() => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });
</script>

<div class="tournaments-tab">
  {#if $isAdmin}
    <div class="tab-head">
      <div class="head-actions">
        <button class="create-btn" onclick={() => (showCreate = true)}>
          <Plus size={12} strokeWidth={2.5} />
          Criar
        </button>
      </div>
    </div>
  {/if}

  {#if $tournamentsError}
    <div class="error" role="alert">{$tournamentsError}</div>
  {/if}

  {#if $tournamentsLoading && $upcomingTournaments.length === 0}
    <!-- Skeleton shimmer no formato das rows — menos "tela morta" que
         um texto "carregando..." -->
    <div class="skeleton" aria-label="carregando torneios">
      <div class="skel-row"></div>
      <div class="skel-row"></div>
      <div class="skel-row"></div>
    </div>
  {:else if $upcomingTournaments.length === 0}
    <div class="empty-card">
      <span class="empty-suits" aria-hidden="true">♠ ♥ ♦ ♣</span>
      <strong>nenhum torneio agendado agora</strong>
      <span class="empty-sub">novos torneios entram no ar ao longo do dia — cola aqui daqui a pouco</span>
    </div>
  {:else}
    <div class="table">
      <div class="thead">
        <span class="th when">Início</span>
        <span class="th name">Nome</span>
        <span class="th inscritos">Status</span>
        <span class="th actions"></span>
      </div>
      <div class="tbody">
        {#each $upcomingTournaments as t, i (t.id)}
          <TournamentTile tournament={t} index={i} />
        {/each}
      </div>
    </div>
  {/if}
</div>

{#if showCreate}
  <CreateTournamentModal onClose={() => (showCreate = false)} />
{/if}

<style>
  .tournaments-tab {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .tab-head {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-bottom: -4px;
  }
  .head-actions { display: flex; gap: 6px; }
  .create-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: linear-gradient(135deg, rgba(255, 210, 74, 0.18), rgba(216, 80, 240, 0.18));
    border: 1px solid rgba(255, 210, 74, 0.6);
    color: var(--warn);
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: 999px;
    cursor: pointer;
    white-space: nowrap;
  }
  .create-btn:hover {
    background: linear-gradient(135deg, rgba(255, 210, 74, 0.3), rgba(216, 80, 240, 0.3));
  }

  .error {
    color: var(--danger);
    background: rgba(255, 76, 126, 0.08);
    border: 1px solid rgba(255, 76, 126, 0.3);
    border-radius: var(--radius-md);
    padding: 10px 12px;
    font-size: 13px;
  }
  /* Empty state cartoon — mesma moldura creme/azul/dourada da tabela,
     pra tela vazia continuar parecendo parte do jogo (nao um erro). */
  .empty-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    padding: 36px 20px;
    text-align: center;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 55%, #f0e7d5 100%);
    border: 4px solid #0D47A1;
    outline: 2px solid #ffb300;
    outline-offset: -3px;
    border-radius: 14px;
    box-shadow: 0 4px 0 #051a3a, 0 6px 18px rgba(13, 71, 161, 0.35);
    color: #0D47A1;
  }
  .empty-suits {
    font-size: 24px;
    letter-spacing: 12px;
    padding-left: 12px; /* compensa o letter-spacing do ultimo char */
    color: #1565C0;
    text-shadow: 0 1px 0 #fff, 0 2px 3px rgba(13, 71, 161, 0.25);
  }
  .empty-card strong {
    font-size: 14px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .empty-sub {
    font-size: 12px;
    color: #1976D2;
    max-width: 380px;
    line-height: 1.45;
  }

  /* Skeleton shimmer — rows fantasma enquanto a lista carrega */
  .skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .skel-row {
    height: 58px;
    border-radius: 12px;
    border: 2px solid rgba(21, 101, 192, 0.28);
    background:
      linear-gradient(100deg,
        rgba(250, 245, 236, 0.05) 30%,
        rgba(250, 245, 236, 0.16) 50%,
        rgba(250, 245, 236, 0.05) 70%);
    background-size: 200% 100%;
    animation: skel-shimmer 1.2s ease-in-out infinite;
  }
  @keyframes skel-shimmer {
    0% { background-position: 120% 0; }
    100% { background-position: -80% 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .skel-row { animation: none; }
  }

  /* Tabela de torneios upcoming - estilo cartoon "puffy" (Felipe 2026-05-26):
     border preta dupla, header gradient profundo com texture, shadow 3D */
  .table {
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    overflow: hidden;
    box-shadow:
      0 4px 0 #051a3a,
      0 6px 18px rgba(13, 71, 161, 0.35);
    border: 4px solid #0D47A1;
    outline: 2px solid #ffb300;
    outline-offset: -3px;
  }
  .thead {
    display: grid;
    grid-template-columns: 90px 1fr 90px 140px;
    gap: 10px;
    padding: 12px 14px;
    background:
      linear-gradient(180deg,
        #1976D2 0%,
        #1565C0 35%,
        #0D47A1 70%,
        #082c69 100%);
    color: #ffd54f;
    font-size: 11px;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    font-weight: 900;
    text-shadow:
      0 -1px 0 rgba(0, 0, 0, 0.7),
      0 1px 2px rgba(0, 0, 0, 0.5);
    border-bottom: 3px solid #ffb300;
    box-shadow:
      inset 0 2px 0 rgba(255, 255, 255, 0.15),
      inset 0 -3px 6px rgba(0, 0, 0, 0.25);
  }
  .thead .inscritos { text-align: center; }
  .thead .actions { text-align: right; }
  .tbody {
    display: flex;
    flex-direction: column;
    background: #d9c9a8;
  }

  @media (max-width: 520px) {
    /* casa com o grid de 3 colunas da row (status some no mobile) */
    .thead {
      grid-template-columns: 52px 1fr auto;
      gap: 8px;
      padding: 10px;
      font-size: 9px;
      letter-spacing: 1px;
    }
    .thead .inscritos { display: none; }
  }
</style>
